/**
 * Recovery codes: 120 random bits written as 24 Crockford base32 characters
 * in six groups of four, such as 7K2M-QX9D-... They are shown once at sign-up
 * and unlock the vault, or a backup, when the password is forgotten.
 */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const RECOVERY_CODE_BYTES = 15;
const CODE_CHARS = (RECOVERY_CODE_BYTES * 8) / 5;

function encode(bytes: Uint8Array): string {
  let bits = 0;
  let value = 0;
  let out = '';
  for (const byte of bytes) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      out += ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  return out;
}

/** Groups a 24-character code as XXXX-XXXX-XXXX-XXXX-XXXX-XXXX. */
export function formatRecoveryCode(code: string): string {
  return (code.match(/.{1,4}/g) ?? []).join('-');
}

/** A new random recovery code, formatted for display. */
export function generateRecoveryCode(): string {
  return formatRecoveryCode(encode(globalThis.crypto.getRandomValues(new Uint8Array(RECOVERY_CODE_BYTES))));
}

/**
 * Reads a recovery code as typed. Ignores case, spaces and dashes, and accepts
 * O for 0 and I or L for 1. Returns the 15 code bytes, or null when the text
 * cannot be a recovery code.
 */
export function parseRecoveryCode(text: string): Uint8Array<ArrayBuffer> | null {
  const cleaned = text
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1');
  if (cleaned.length !== CODE_CHARS) return null;
  const bytes = new Uint8Array(RECOVERY_CODE_BYTES);
  let bits = 0;
  let value = 0;
  let index = 0;
  for (const char of cleaned) {
    const digit = ALPHABET.indexOf(char);
    if (digit < 0) return null;
    value = ((value << 5) | digit) & 0xffff;
    bits += 5;
    if (bits >= 8) {
      bytes[index] = (value >>> (bits - 8)) & 0xff;
      index += 1;
      bits -= 8;
    }
  }
  return bytes;
}
